import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import {
  useListOrderReceipts,
  getListOrderReceiptsQueryKey,
  useGeneratePlanPeriodReceipt,
  useMarkPlanPeriodPaid,
  type OrderReceiptRegisterItem,
  type ListOrderReceiptsParams,
} from "@workspace/api-client-react";
import { ListPagination } from "@/components/common/list-pagination";
import { useQueryClient } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  CheckCircle, Download, Eye, Loader2, MessageCircle, Mail, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

type FilterChip = "All" | "Unpaid" | "Partial" | "Paid" | "Overdue";

const INR = (n: number) =>
  `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n)}`;

// Current calendar month in IST as YYYY-MM (en-CA formats as YYYY-MM-DD).
function currentIstMonth(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(new Date())
    .slice(0, 7);
}

function fmtDate(d: string): string {
  const parsed = new Date(`${d.slice(0, 10)}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? d : format(parsed, "dd MMM yyyy");
}

// Order-receipt status badge (Unpaid | Partial | Paid, with a derived Overdue).
function ReceiptStatus({ item }: { item: OrderReceiptRegisterItem }) {
  const today = new Date().toISOString().slice(0, 10);
  const anchor = (item.reminderDate ?? item.periodStart).slice(0, 10);
  const overdue = item.status !== "Paid" && anchor < today;
  const label = overdue ? "Overdue" : item.status;
  const cls =
    label === "Paid"
      ? "bg-green-100 text-green-700 border-green-200"
      : label === "Partial"
        ? "bg-blue-50 text-blue-600 border-blue-100"
        : label === "Overdue"
          ? "bg-red-100 text-red-700 border-red-200"
          : "bg-zinc-100 text-zinc-600 border-zinc-200";
  return <Badge variant="outline" className={`${cls} text-xs`}>{label}</Badge>;
}

function SummaryTile({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <Card className="flex-1 p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${tone ?? ""}`}>{value}</p>
    </Card>
  );
}

export function InvoiceQueue() {
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  // Invoice Queue defaults to issued invoices that are settled (Paid); the other
  // chips (Unpaid / Partial / Overdue) remain one click away.
  const [chip, setChip] = useState<FilterChip>("Paid");
  const [search, setSearch] = useState("");
  // Default to the current running month; "" means all months.
  const [month, setMonth] = useState<string>(currentIstMonth());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [viewing, setViewing] = useState<OrderReceiptRegisterItem | null>(null);

  const trimmed = search.trim();
  const params: ListOrderReceiptsParams = {
    ...(chip === "Unpaid" || chip === "Partial" || chip === "Paid" ? { status: chip } : {}),
    ...(chip === "Overdue" ? { overdue: true } : {}),
    ...(month ? { month } : {}),
    ...(trimmed ? { q: trimmed } : {}),
    page,
    pageSize,
  };
  const { data, isLoading } = useListOrderReceipts(params, {
    query: { queryKey: getListOrderReceiptsQueryKey(params) },
  });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const summary = data?.summary;

  const markPaid = useMarkPlanPeriodPaid();

  // Reset to page 1 when filters change.
  useEffect(() => { setPage(1); }, [chip, search, month, pageSize]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: getListOrderReceiptsQueryKey() });
  }

  async function onMarkPaid(item: OrderReceiptRegisterItem) {
    try {
      await markPaid.mutateAsync({ periodId: item.periodId, data: { paid: true } });
      toast.success(`Receipt ${item.receiptNumber} marked paid`);
      invalidate();
    } catch (err) {
      toast.error(`Couldn't mark paid: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-4">
      {/* Totals across the filtered set */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <SummaryTile label="Total billed" value={INR(Number(summary?.totalBilled ?? 0))} />
        <SummaryTile label="Paid this month" value={INR(Number(summary?.paidThisMonth ?? 0))} tone="text-green-600" />
        <SummaryTile label="Outstanding" value={INR(Number(summary?.totalOutstanding ?? 0))} tone="text-amber-600" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(["All", "Unpaid", "Partial", "Paid", "Overdue"] as FilterChip[]).map((c) => (
          <Button key={c} size="sm" variant={chip === c ? "default" : "outline"} onClick={() => setChip(c)}>
            {c}
          </Button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Input
            type="month"
            aria-label="Billing month"
            className="w-[10rem]"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
          {month ? (
            <Button size="sm" variant="ghost" onClick={() => setMonth("")}>
              All months
            </Button>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setMonth(currentIstMonth())}>
              This month
            </Button>
          )}
          <Input
            placeholder="Search account or receipt no…"
            className="max-w-xs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Receipt #</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Plan · Period</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Received</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.periodId}>
                  <TableCell className="font-mono">{item.receiptNumber}</TableCell>
                  <TableCell>
                    <button
                      className="text-left font-medium text-primary hover:underline"
                      onClick={() => navigate(`/accounts/${item.accountId}`)}
                    >
                      {item.accountName}
                    </button>
                  </TableCell>
                  <TableCell className="text-xs">
                    <span className="font-medium">{item.planName ?? "—"}</span>
                    <span className="block text-muted-foreground">
                      {fmtDate(item.periodStart)} → {fmtDate(item.periodEnd)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono">{INR(Number(item.amount))}</TableCell>
                  <TableCell className="text-right font-mono">
                    {Number(item.amountPaid) > 0 ? INR(Number(item.amountPaid)) : "—"}
                  </TableCell>
                  <TableCell className={`text-right font-mono ${Number(item.balance) > 0 ? "text-amber-600" : ""}`}>
                    {Number(item.balance) > 0 ? INR(Number(item.balance)) : "—"}
                  </TableCell>
                  <TableCell><ReceiptStatus item={item} /></TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" aria-label="View receipt" onClick={() => setViewing(item)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {item.status !== "Paid" ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Mark paid"
                          disabled={markPaid.isPending}
                          onClick={() => onMarkPaid(item)}
                        >
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </Button>
                      ) : null}
                      {item.receiptPdfUrl ? (
                        <a href={item.receiptPdfUrl} target="_blank" rel="noreferrer">
                          <Button size="icon" variant="ghost" aria-label="Download PDF">
                            <Download className="h-4 w-4" />
                          </Button>
                        </a>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    No order receipts match your filters.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          {total > 0 ? (
            <div className="px-4">
              <ListPagination
                total={total}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                isLoading={isLoading}
                noun={["receipt", "receipts"]}
              />
            </div>
          ) : null}
        </Card>
      )}

      {viewing ? (
        <OrderReceiptViewDialog
          item={viewing}
          onClose={() => setViewing(null)}
          onChanged={invalidate}
        />
      ) : null}
    </div>
  );
}

// View / share one issued order receipt. Regenerates the PDF on open (the
// receipt number stays the one already issued) and reuses the per-period
// generate endpoint for WhatsApp / Email delivery.
function OrderReceiptViewDialog({
  item,
  onClose,
  onChanged,
}: {
  item: OrderReceiptRegisterItem;
  onClose: () => void;
  onChanged: () => void;
}) {
  const gen = useGeneratePlanPeriodReceipt();
  const [pdfUrl, setPdfUrl] = useState<string | null>(item.receiptPdfUrl ?? null);
  const [busy, setBusy] = useState<null | "load" | "whatsapp" | "email">("load");

  useEffect(() => {
    let cancelled = false;
    gen
      .mutateAsync({ periodId: item.periodId, data: {} })
      .then((r) => { if (!cancelled) setPdfUrl(r.pdfUrl ?? null); })
      .catch(() => { if (!cancelled) toast.error("Couldn't load the receipt."); })
      .finally(() => { if (!cancelled) setBusy(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.periodId]);

  async function share(channel: "whatsapp" | "email") {
    setBusy(channel);
    try {
      const r = await gen.mutateAsync({ periodId: item.periodId, data: { channel } });
      if (r.pdfUrl) setPdfUrl(r.pdfUrl);
      onChanged();
      toast[r.sent ? "success" : "message"](
        channel === "whatsapp"
          ? r.sent ? "Shared on WhatsApp." : "WhatsApp not configured or no number on file."
          : r.sent ? "Receipt emailed." : "Email not configured or no address on file.",
      );
    } catch {
      toast.error("Couldn't share the receipt.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Order Receipt · {item.receiptNumber}</DialogTitle>
          <DialogDescription>
            {item.accountName} — {item.planName ?? "Plan"} · {fmtDate(item.periodStart)} → {fmtDate(item.periodEnd)}
          </DialogDescription>
        </DialogHeader>

        <div className="h-[62vh] overflow-hidden rounded-md border bg-muted/10">
          {pdfUrl ? (
            <iframe
              key={pdfUrl}
              src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
              title="Order receipt"
              className="h-full w-full"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {busy === "load" ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Generating receipt…
                </span>
              ) : (
                "No preview available."
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-wrap gap-2 sm:justify-start">
          <Button variant="outline" size="sm" disabled={!pdfUrl} onClick={() => pdfUrl && window.open(pdfUrl, "_blank", "noopener")}>
            <ExternalLink className="mr-1.5 h-4 w-4" /> Open PDF
          </Button>
          <Button variant="outline" size="sm" disabled={!!busy} onClick={() => share("whatsapp")}>
            <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
          </Button>
          <Button variant="outline" size="sm" disabled={!!busy} onClick={() => share("email")}>
            <Mail className="mr-1.5 h-4 w-4" /> Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
